import i18n from 'i18next';

import { languageStateToLocalesMap } from 'src/constants/i18n.constants';
import { LOCALES } from 'src/interfaces/i18n.types';
import { LanguageState } from 'src/store/api/client-state/types';

export interface LocaleResource {
  [key: string]: string;
}

export interface LoadedLocales {
  [locale: string]: LocaleResource;
}

// Use Map for better performance and immutability patterns
const loadedLocales = new Map<LOCALES, LocaleResource>();

export const loadLocale = async (locale: LOCALES): Promise<LocaleResource> => {
  // Return cached locale if already loaded
  if (loadedLocales.has(locale)) {
    return loadedLocales.get(locale)!;
  }

  try {
    // Dynamic import based on locale
    const localeData = await import(`../../locales/${locale}.json`);
    const translations = localeData.default || localeData;

    // Cache the loaded locale
    loadedLocales.set(locale, translations);

    return translations;
  } catch (error) {
    const enLocaleResource = loadedLocales.get(LOCALES.EN);

    // Return English as fallback if available, otherwise empty object
    if (locale !== LOCALES.EN && enLocaleResource) {
      return enLocaleResource;
    }

    return {};
  }
};

// Function to change language dynamically
export const changeLanguage = async (locale: LOCALES): Promise<void> => {
  try {
    // Load the locale if not already loaded
    const translations = await loadLocale(locale);

    // Add the resource bundle to i18next
    i18n.addResourceBundle(locale, 'translation', translations, true, true);

    // Change the language
    await i18n.changeLanguage(locale);
  } catch (error) {
    console.warn(`Failed to change language to ${locale}:`, error);
    // Fallback to English if language change fails
    await i18n.changeLanguage(LOCALES.EN);
  }
};

export const getSelectedLanguageLocale = (
  selectedLanguage?: string,
): LOCALES | null => {
  if (!selectedLanguage) return null;

  const selectedLanguageLocale = languageStateToLocalesMap[selectedLanguage];

  return selectedLanguageLocale ?? null;
};

export const handleLanguageChange = (
  currentLanguageState: LanguageState | undefined,
) => {
  const selectedLanguageLocale = getSelectedLanguageLocale(
    currentLanguageState?.selectedLanguage,
  );

  if (!selectedLanguageLocale) return;

  changeLanguage(selectedLanguageLocale);
};
